import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { QuickRenewFormVm } from '../../modules/quick-renew/services/quick-renew-form.vm';
import { catchError, filter, map } from 'rxjs/operators';
import { QuickRenewActions } from './quick-renew.actions';
import { exhaustMap, of, switchMap, throwError, withLatestFrom } from 'rxjs';
import { Operation, OperationExecutePayload } from '@aaa/interface-joinRenew-joinRenewLib';
import {
  getQuickRenewAccountDetails,
  getQuickRenewExecutionData,
  QuickRenewAccountDetails,
} from './quick-renew.selectors';
import { FormGroupValue } from '../../modules/share/form.utils';
import { getPayment } from '@aaa/emember/store-payment';
import { PaymentForm } from '@aaa/emember/share/payment-form';
import { AccountDetails, ValidateSucceededResponseObject } from '../types/types';
import { ExecuteService } from '../services/execute.service';
import { getClearCacheSettings } from '../utils/get-cache-settings';
import { Cybersource } from '../cybersource.type';
import { DataLayerService } from '../../modules/share/services/data-layer.service';
import { AppAnalyticsEvents, ClubApp } from '@aaa/emember/types';
import { getTransactionId } from '../utils/get-transaction-id';
import { filterByClubIds } from '../utils/filter-by-club-ids';
import {
  MembershipCampanaGetCostsRenewEvent,
  MembershipCampanaGetCostsResponse,
  MembershipCampanaMethod,
  MembershipCampanaOperationExecuteEvent,
} from '@aaa/interface-joinRenew-membership-membershipCampana';
import { CampanaType } from '../campana.type';
import { buildFreedompayPaymentEvent, getCampanaJoinPayErrorType } from '../campana-utils';
import { checkMembershipErrorsCampanaSystem } from '../check-membership-errors-campana-system';
import { RequestError, RequestErrorType } from '../generic-errors';
import { checkCampanaPaymentValidation } from '../check-campana-payment-validation';

// The quick renew card takes the household digits (the AXIS client id) and optionally the 2 person
// digits after them (00 for the primary). AXIS looks the membership up by the household alone
const householdNumber = (membershipNumber = '') => membershipNumber.slice(0, 7);

/**
 * The anonymous /renew flow for Northway: three point identity check, then a pay-only page.
 *
 * Two campana calls carry it. GET_COSTS_RENEW proves last name and postcode against the AXIS
 * client summary, opens a calc session on the existing membership and answers with the quote,
 * the member's identity and the summary the page names its rows from - no member record is
 * minted, unlike a join recost. OPERATION_EXECUTE with Operation.RENEW then promotes that
 * session, posts the freedompay token as the payment and completes it.
 */
@Injectable({ providedIn: 'root' })
export class QuickRenewCampanaSystemEffects {
  store = inject(Store);
  actions$ = inject(Actions).pipe(filterByClubIds([ClubApp.Northway]));
  formVm = inject(QuickRenewFormVm);
  executeService = inject(ExecuteService);
  dataLayer = inject(DataLayerService);

  recostValidateMembership$ = createEffect(() =>
    this.actions$.pipe(
      ofType(QuickRenewActions.recostValidate),
      switchMap(() =>
        this.recostValidate().pipe(
          map((res) => QuickRenewActions.recostValidateSucceeded(res)),
          catchError((error) => of(QuickRenewActions.recostValidateFailed({ error }))),
        ),
      ),
    ),
  );

  setSummaries$ = createEffect(() =>
    this.actions$.pipe(
      ofType(QuickRenewActions.recostValidateSucceeded),
      map(({ response }: ValidateSucceededResponseObject<MembershipCampanaGetCostsResponse>) => {
        /*
            Everything the payment page shows comes from here. The names and the associate rows
            come from the summary the recost returned; the level, expiry and total come from the
            calc session it opened. The balance is AXIS's own dues total, which is the number
            promoteAndComplete compares the charge against, so the page never recomputes it.
        */
        const accountDetails = new CampanaType.AccountInfo(
          response.identity?.memberNumber || householdNumber(this.formVm.membershipFormGroup.value.membershipNumber),
          response.summary,
          response,
        );

        return QuickRenewActions.setAccountDetails({ accountDetails });
      }),
    ),
  );

  // the quote went stale between the summary and Pay, and nothing was charged: price the
  // membership again so the member sees what AXIS would take now and can pay that. The form
  // still holds the three fields, and the reducer clears executionData on a new recost, so Pay
  // cannot resend the dead quote in the meantime
  recostAfterPriceChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(QuickRenewActions.payFailed),
      filter(({ error }) => error.type === RequestErrorType.JoinPriceChanged),
      map(() => QuickRenewActions.recostValidate()),
    ),
  );

  pay$ = createEffect(() =>
    this.actions$.pipe(
      ofType(QuickRenewActions.pay),
      withLatestFrom(
        this.store.select(getQuickRenewAccountDetails),
        this.store.select(getQuickRenewExecutionData),
        this.store.select(QuickRenewAccountDetails.getBalance),
        this.store.select(getPayment),
      ),
      // exhaustMap, not switchMap: a second Pay while the first is in flight is a double charge,
      // not a newer intent
      exhaustMap(([, accountDetails, executionData, totalCost, payment]) =>
        this.pay(accountDetails, executionData, totalCost, payment).pipe(
          map(() => QuickRenewActions.paySucceeded()),
          catchError((error) => of(QuickRenewActions.payFailed({ error }))),
        ),
      ),
    ),
  );

  pay(
    accountDetails: AccountDetails | null,
    executionData: string,
    totalCost: number,
    payment: { token: string; formValues: FormGroupValue<PaymentForm> },
  ) {
    if (!executionData) {
      // there is no quote to execute - the recost failed, or the member reached Pay without one -
      // and campana would refuse the execute outright
      return throwError(() => new RequestError(RequestErrorType.RenewMembership));
    }

    /*
        Northway pays through freedompay: the opaque token CREATE_TOKEN returned, plus the total
        the member was actually shown, which promoteAndComplete checks AXIS's price against before
        it charges anything. No token means no instrument and the event is left off entirely,
        which is how a $0 renew completes without a charge.
    */
    const paymentEvent = buildFreedompayPaymentEvent(Operation.RENEW, payment.token, totalCost);

    const membershipEvent: MembershipCampanaOperationExecuteEvent = {
      cacheSettings: getClearCacheSettings(),
      executionData: executionData,
      method: MembershipCampanaMethod.OPERATION_EXECUTE,
      operation: Operation.RENEW,
    };

    const payload: OperationExecutePayload = {
      operation: Operation.RENEW,
      membershipEvent: membershipEvent,
      paymentEvent: paymentEvent,
    };

    return this.executeService
      .execute<CampanaType.OperationExecuteResponseObject, Cybersource.ExecutePaymentResponseObject>(payload)
      .pipe(
        map(({ validateObject, paymentObject, operationObject }) => {
          if (paymentObject?.meta?.isError || paymentObject?.error) {
            checkCampanaPaymentValidation(paymentObject.error || paymentObject);
          }

          if (validateObject?.error) {
            /*
                AXIS takes the money at PUT session/complete, so everything that fails before it
                charged nothing. Price drift and a session rebuilt at a different price come back
                as JoinPriceChanged, whose copy is flow neutral and which recostAfterPriceChange$
                keys off. COMPLETION_UNCERTAIN - the one code that may mean money moved - still
                reads as a generic failure until QUICK-RENEW-PLAN.md §3.6 gives it its own message.
            */
            throw new RequestError(getCampanaJoinPayErrorType(validateObject.error), validateObject);
          }

          if (operationObject?.error) {
            throw new RequestError(RequestErrorType.OperationError, operationObject);
          }

          this.dataLayer.purchaseEvent(AppAnalyticsEvents.QuickRenewRenewal, {
            itemId: 'primary',
            itemQuantity: 1,
            pageName: AppAnalyticsEvents.QuickRenewRenewal,
            // AXIS's receipt number is the only real transaction id on this flow: getTransactionId
            // invents a Math.random() one for anything that is not cybersource
            transactionId: validateObject?.response?.response?.receiptNumber || getTransactionId(paymentObject),
            itemPrice: totalCost,
            context: 'ava-store ' + AppAnalyticsEvents.QuickRenewRenewal,
            itemMembershipLevel: accountDetails?.code?.level,
          });

          return { membership: validateObject, payment: paymentObject };
        }),
      );
  }

  recostValidate() {
    const formValues = this.formVm.membershipFormGroup.value;
    const payload: MembershipCampanaGetCostsRenewEvent = {
      method: MembershipCampanaMethod.GET_COSTS_RENEW,
      memberNumber: householdNumber(formValues?.membershipNumber),
      // anonymous: the member proves who they are with the last name and postcode AXIS holds,
      // instead of a login. A mismatch is deliberately indistinguishable from a wrong number
      verificationData: {
        lastName: formValues?.lastName || '',
        postalCode: formValues?.zipcode || '',
      },
    };

    return this.executeService.membershipQuery<CampanaType.RecostResponseObject>(payload).pipe(
      map((validateObject) => {
        /*
            A refused quote has to stop the step machine. executionData sits on the response, not
            on the session doc around it, so reading it off the doc dispatched a "succeeded" with
            no executionData for every recost - a wrong last name included - and checkPayment
            bounced the member back to /renew with nothing said.
        */
        if (validateObject.meta?.isError || !validateObject.response?.executionData) {
          checkMembershipErrorsCampanaSystem(validateObject.error, validateObject, RequestErrorType.MembershipError);
          // the map throws for every code it knows; a refusal that carries none still cannot go on
          throw new RequestError(RequestErrorType.MembershipError, validateObject);
        }

        return {
          executionData: validateObject.response.executionData,
          response: validateObject.response,
        };
      }),
    );
  }
}
